import { PhoenixMetrics } from "@amzn/ads-phoenix-type-script-client";
import { InsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { TextButton } from "@amzn/storm-ui";
import { useAsync } from "@react-hookz/web";
import { useCallback, useState } from "react";
import React from "react";
import { useStore } from "zustand";

import { ComplianceState } from "../../../../api/adsApi/adsApi.interfaces";
import { getDiscoveryApiClient } from "../../../../api/discoveryApi/discoveryApi";
import { getG4SClient } from "../../../../api/g4sApi/g4sApi";
import type { FeedbackReason } from "../../../../api/g4sApi/types/operations";
import { getSeleneApiClient } from "../../../../api/seleneApi/seleneApi";
import { INSIGHT_DETAILS_TRIGGER_TEXT } from "../../../../config/insights.config";
import {
    G4S_FEEDBACK_REASONS_THUMBS_DOWN,
    G4S_FEEDBACK_REASONS_THUMBS_UP,
} from "../../../../constants/feedbackReasons";
import { G4sPlacement } from "../../../../constants/g4sPlacement";
import { TELEMETRY_ID, PHOENIX_FEATURE } from "../../../../constants/telemetry";
import {
    MarketingNarrativeDetailsType,
    getMarketingNarrativeDetails,
} from "../../../../hooks/marketingContent/getMarketingNarrativeDetails";
import { useInsightIdentifierEffect } from "../../../../hooks/misc/useInsightIdentifierEffect";
import { useAsyncErrorStatusNotification } from "../../../../hooks/notifications/useAsyncErrorStatusNotification";
import { authStore } from "../../../../store/auth";
import { FeatureFlagsStore } from "../../../../store/featureFlags";
import { discoveryFilterStore } from "../../../../store/filters";
import { globalWorkbenchStore } from "../../../../store/globalWorkbench";
import { insightDetailsStore } from "../../../../store/insightDetails";
import { navigationStore } from "../../../../store/navigation";
import {
    notificationStore,
    NotificationType,
} from "../../../../store/notification";
import { reportIssueStore } from "../../../../store/reportIssue";
import { workbenchStore } from "../../../../store/workbench";
import { AquilaThumbsFeedbackPlacement } from "../../../../types/aquila";
import { ContentPreviewType } from "../../../../types/content";
import type {
    FeedbackAttributes,
    FeedbackInsightIdentifier,
    FeedbackMetadata,
} from "../../../../types/feedback";
import type { MarketingNarrativeFileFormats } from "../../../../types/marketingNarrativeFileFormats";
import { submitFeedback } from "../../../../utils/feedback.utils";
import { getInsightPreviewFileFormat } from "../../../../utils/insightPreview.utils";
import { getAdvertiserId } from "../../../../utils/permission.utils";
import { getPhoenixContentInfoFromStore } from "../../../../utils/phoenix.utils";
import { isExploreWithAITab } from "../../../../utils/tab.utils";
import {
    logViewInsightDetailsClick,
    logWithTakt,
} from "../../../../utils/telemetry.utils";
import { AquilaThumbsFeedback } from "../../../aquilaThumbsFeedback/AquilaThumbsFeedback";
import { StyledActionsContainer } from "../../commonStyles";
import { InsightSelectionActionText } from "../InsightSelectionActionText";

interface MarketingNarrativeCardActionProps {
    identifier: string;
    index?: number;
    g4sResponseId?: string;
}

export const MarketingNarrativeCardAction: React.FunctionComponent<
    MarketingNarrativeCardActionProps
> = (props: MarketingNarrativeCardActionProps) => {
    const { identifier, index, g4sResponseId } = props;
    const insightType = InsightType.G4S;
    const [setInsightDetails, setAsyncStatus, setDownloadInfo] = useStore(
        insightDetailsStore,
        (state) => [
            state.setInsightDetails,
            state.setAsyncStatus,
            state.setDownloadInfo,
        ],
    );
    const [useUnifiedSearch] = useStore(FeatureFlagsStore, (state) => [
        state.unifiedSearch,
    ]);

    const [getSlidesSelected] = useStore(globalWorkbenchStore, (state) => [
        state.getSlidesSelected,
    ]);
    const activeTab = useStore(navigationStore, (state) => state.activeTab);
    const [getSlideIndexes] = useStore(workbenchStore, (state) => [
        state.getSlideIndexes,
    ]);

    const [updateReportIssueContextData] = useStore(
        reportIssueStore,
        (state) => [state.updateReportIssueContextData],
    );

    const [upsertNotification] = useStore(notificationStore, (state) => [
        state.upsertNotification,
    ]);

    const [contentSubType, filters] = useStore(
        discoveryFilterStore,
        (state) => [state.getContentSubType(), state.getFilters()],
    );
    const contentsData = filters[contentSubType.provider].data;

    const [contentTitle, setContentTitle] = useState("");

    const [feedbackState, feedbackAction] = useAsync(
        async (
            feedbackReasons: FeedbackReason[],
            freeFormFeedback: string,
            rating: number,
        ) => {
            if (g4sResponseId) {
                await (
                    await getG4SClient()
                ).createFeedback({
                    responseId: g4sResponseId,
                    feedbackReasons,
                    freeFormFeedback,
                    rating,
                    dataContext: {
                        type: "ADVERTISER_INSIGHTS",
                        content: contentTitle,
                        id: identifier,
                    },
                    placement: G4sPlacement.EXPLORE_WITH_AI_PAGE,
                });
                upsertNotification(
                    "Feedback provided",
                    NotificationType.SUCCESS,
                    identifier,
                );
            }
        },
    );
    useAsyncErrorStatusNotification(feedbackState);

    const feedbackInsightIdentifier: FeedbackInsightIdentifier = {
        identifier,
        insightType,
    };

    const feedbackMetadata: FeedbackMetadata = {
        responseId: g4sResponseId || "",
    };

    const handleFeedbackSubmit = (attr: FeedbackAttributes) => {
        const { reasons: selectedReasons, feedback, rating } = attr;
        const reasons = selectedReasons.map((reason) => ({ reason }));
        void feedbackAction.execute(reasons, feedback, rating);
        submitFeedback(feedbackInsightIdentifier, feedbackMetadata, rating);
        logWithTakt(TELEMETRY_ID.G4S_MARKETING_CONTENT_FEEDBACK_SUBMITTED, {
            reasons,
            feedback,
            rating,
            identifier,
            insightType,
            responseId: g4sResponseId,
        });
        PhoenixMetrics.emit(
            TELEMETRY_ID.G4S_MARKETING_CONTENT_FEEDBACK_SUBMITTED,
            PHOENIX_FEATURE.FEEDBACK,
            {
                contentInfo: getPhoenixContentInfoFromStore(
                    InsightType.MARKETING,
                    identifier,
                ),
                customData: {
                    reasons,
                    feedback,
                    rating,
                    responseId: g4sResponseId,
                },
            },
        );
    };

    const [contentAsyncState, contentAsyncActions] = useAsync(async () => {
        const data = await (
            await getDiscoveryApiClient()
        ).getContentDetails(identifier, ContentPreviewType.PDF, true);

        const pptxData = await (
            await getDiscoveryApiClient()
        ).getContentDetails(identifier, ContentPreviewType.ORIGINAL);

        let seleneData;
        try {
            seleneData = await (
                await getSeleneApiClient()
            ).getContentDetails({
                AmazonAdvertisingAPIClientId: "InsightsHub",
                contentId: identifier,
                ContentFileType: ContentPreviewType.PDF,
                isUserPreview: true,
                userAlias: authStore.getState().username,
            });
        } catch (error) {
            upsertNotification(
                "Failed to return an AI summary. Please use the Summarize Deck button instead.",
                NotificationType.ERROR,
            );
            seleneData = { content: { aiSummary: "", aiTitle: "" } };
        }

        const advertiserId = getAdvertiserId();

        const details = getMarketingNarrativeDetails(
            data.content!,
            data.contentUsers!,
            MarketingNarrativeDetailsType.SHEET,
        );

        const insightFileFormat = data.content
            ?.fileFormat as MarketingNarrativeFileFormats;

        const compliantWorkbenchStatuses = [
            ComplianceState.COMPLIANT,
            ComplianceState.UNSUPPORTED_MEDIA_TYPE,
        ];

        const title = data.content?.contentTitle ?? "";
        setContentTitle(title);

        updateReportIssueContextData({
            identifier,
            title,
            insightType: InsightType.MARKETING,
        });

        setInsightDetails({
            info: {
                title,
                identifier: data.content?.contentId ?? "",
                previewFileFormat:
                    getInsightPreviewFileFormat(insightFileFormat),
                details,
                insightType: InsightType.MARKETING,
                content: {
                    ...data.content,
                    aiSummary: seleneData.content?.aiSummary,
                    aiTitle: seleneData.content?.aiTitle,
                },
                previewAsset: {
                    downloadUrl: data.downloadUrl,
                    content: {
                        bucket: "",
                        key: "",
                    },
                },
                compliance: {
                    isCompliant: advertiserId
                        ? compliantWorkbenchStatuses.includes(
                              data.content
                                  ?.workbenchComplianceState as ComplianceState,
                          )
                        : false,
                    status: advertiserId
                        ? (data.content?.workbenchComplianceState ??
                          ComplianceState.UNSUPPORTED_PPTX)
                        : ComplianceState.UNSUPPORTED_VIEW,
                },
                ...(g4sResponseId && {
                    feedback: {
                        feedbackOptions: {
                            thumbsDown: {
                                handler: handleFeedbackSubmit,
                                reasons: G4S_FEEDBACK_REASONS_THUMBS_DOWN,
                            },
                            thumbsUp: {
                                handler: handleFeedbackSubmit,
                                reasons: G4S_FEEDBACK_REASONS_THUMBS_UP,
                            },
                        },
                        insightIdentifier: feedbackInsightIdentifier,
                        metadata: feedbackMetadata,
                    },
                }),
            },
            open: true,
            asyncStatus: contentAsyncState.status,
        });
        setDownloadInfo({
            downloadUrl: pptxData.downloadUrl,
            content: {
                bucket: "",
                key: "",
            },
            contentId: data.content?.contentId ?? "",
            contentVersion: data.content?.latestVersion
                ? data.content?.latestVersion.toString()
                : "1",
        });
        return data;
    });
    useAsyncErrorStatusNotification(contentAsyncState, [], {
        Error: "Your insight failed to download. If issue persists, please Report an issue",
    });

    const handleClick = useCallback(async () => {
        logViewInsightDetailsClick(
            identifier,
            (index ?? 0) + 1,
            contentsData.length,
            undefined,
            useUnifiedSearch,
        );
        setAsyncStatus("loading");
        void contentAsyncActions.execute();
    }, [identifier, index, contentsData.length]);

    useInsightIdentifierEffect(identifier, handleClick);
    const numSelectedSlides = getSlidesSelected(identifier).length;

    return (
        <StyledActionsContainer>
            <TextButton onClick={() => void handleClick()}>
                {INSIGHT_DETAILS_TRIGGER_TEXT}
            </TextButton>
            {numSelectedSlides > 0 && (
                <InsightSelectionActionText
                    identifier={identifier}
                    numSelectedSlides={numSelectedSlides}
                />
            )}
            {numSelectedSlides === 0 && isExploreWithAITab(activeTab) && (
                <AquilaThumbsFeedback
                    identifier={identifier}
                    insightType={InsightType.MARKETING}
                    placement={AquilaThumbsFeedbackPlacement.CARD}
                    configId={"G4sThumbsFeedbackConfig_StormV4"}
                />
            )}
        </StyledActionsContainer>
    );
};
